import {Prose} from "@client/components/Prose";
import {SubscriptionDetail} from "@client/components/SubscriptionDetail";
import * as healthkit from "@client/features/healthkit";
import {fetchProfile, getCredentials, getSubscription} from "@client/features/orm";
import * as subscriptions from "@client/features/subscriptions";
import * as forms from "@client/forms";
import {TextInput} from "@client/forms";
import * as hardware from "@client/hardware";
import {useAsync} from "@client/hooks";
import * as router from "@client/router";
import * as typography from "@client/shared/typography";
import {ImperialCaloriesContext} from "@client/site/context";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import {bindActionCreators} from "@reduxjs/toolkit";
import React from "react";

export const Profile = router.Profile.withHooks((props) => {
    const [endpoint, setEndpoint] = React.useState(utils.getAPIEndpoint());
    const {profile} = props;
    const [healthKitStatus, setHealthKitStatus] = React.useState<string | null>(null);
    const [authorizationStatuses, setAuthorizationStatuses] = React.useState<
        Awaited<ReturnType<typeof healthkit.checkPermissions>>
    >([]);

    React.useEffect(() => {
        props.dispatch(subscriptions.loadStripe());
    }, []);

    const {setIsImperial} = React.useContext(ImperialCaloriesContext);

    // A stored choice the column never checked reads back as unselected, its
    // text kept as a draft so the form is not ready until someone picks.
    const gender = forms.asOption(
        server.core.rpc.UpdateProfile.fields.gender.options,
        profile.gender,
        {missing: null, unselected: ""},
    );
    const activity = forms.asOption(
        server.core.rpc.UpdateProfile.fields.activity.options,
        profile.activity,
        {missing: null, unselected: ""},
    );
    const goal = forms.asOption(
        server.core.rpc.UpdateProfile.fields.goal.options,
        profile.goal,
        {missing: null, unselected: null},
    );

    const form = forms.useForm(server.core.rpc.UpdateProfile, {
        initial: {
            ...profile,
            name: profile.name ?? "",
            email: profile.user.email,
            gender: gender.value,
            activity: activity.value,
            goal: goal.value,
        },
        drafts: {gender: gender.draft, activity: activity.draft, goal: goal.draft},
    });
    const showSettings = form.values.name.toLowerCase() == "joy debug";
    const dataDirectory = useAsync(hardware.dataDirectory);

    const platform = props.useSelector((state) => state.boot.platform);

    React.useEffect(() => {
        if (platform === "ios") {
            void (async () => {
                const status =
                    await hardware.HealthPlugin.getRequestStatusForAuthorization(
                        hardware.HEALTHKIT_PERMISSIONS,
                    ).catch(() => undefined);
                setHealthKitStatus(status?.status ?? null);

                // Get individual authorization statuses for debugging
                const statuses = await healthkit.checkPermissions();
                setAuthorizationStatuses(statuses);
            })();
        }
    }, [platform]);

    React.useEffect(() => {
        setIsImperial(form.values.is_imperial);

        return () => {
            setIsImperial(null);
        };
    }, [form.values.is_imperial]);

    return {
        ...props.useSelector((state) => ({
            subscription: getSubscription(state),
            isShowingPayPal: state.subscriptions.isShowingPayPal,
            subscriptionDollarsPerMonth:
                getCredentials(state)!.SUBSCRIPTION_DOLLARS_PER_MONTH,
            canUsePayPal: window.cordova == null,
            platform: state.boot.platform!,
        })),
        ...bindActionCreators(subscriptions.actions, props.dispatch),
        debug: props.useSelector((state) => state.boot),
        showSettings,
        form,
        endpoint,
        setEndpoint,
        dataDirectory,
        healthKitStatus,
        authorizationStatuses,
    };
})
    .withTab(
        {
            name: "profile",
            icon: "user",
            title: "Profile",
        },
        (props) => (
            <div style={{display: "flex", flexDirection: "column", gap: 15}}>
                <p>Tell us a little bit about yourself.</p>
                <forms.Field field={props.form.fields.email} />
                <forms.Field field={props.form.fields.name} />
                <forms.Field field={props.form.fields.date_of_birth} />
                <forms.Field field={props.form.fields.is_imperial} />
                <forms.Field field={props.form.fields.use_kilojoules} />
                <forms.Field field={props.form.fields.weight} />
                <forms.Field field={props.form.fields.height} />
                <forms.Field field={props.form.fields.waist} />
                <forms.Field field={props.form.fields.gender} />
                <forms.Field field={props.form.fields.body_fat} />
                <forms.Field field={props.form.fields.activity} />
            </div>
        ),
    )
    .withTab(
        {
            name: "subscription",
            icon: "credit-card",
            title: "Subscription",
            guard: (props) => {
                if (
                    (props.subscription != null && props.subscription.type != "none") ||
                    !props.profile.user.is_trial_active
                ) {
                    return props;
                }
                return false;
            },
        },
        (props) => <SubscriptionDetail {...props} />,
    )
    .withTab(
        {
            name: "manager",
            icon: "0291-users",
            title: "My Manager",
            guard: (props) => {
                if (props.profile.user.is_client === true) {
                    return props;
                }
                return false;
            },
        },
        () => (
            <Prose>
                <typography.P>
                    You currently have people managing or asking to manage your journal.
                </typography.P>
                <typography.P>
                    You can email <strong>support@joyapp.com</strong> and request that
                    we revoke their access.
                </typography.P>
            </Prose>
        ),
    )
    .withTab(
        {
            name: "settings",
            icon: "0115-cog",
            title: "Settings",
            guard: (props) => {
                if (props.showSettings === true) {
                    return props;
                }

                return false;
            },
        },
        ({
            debug,
            endpoint,
            setEndpoint,
            dataDirectory,
            healthKitStatus,
            authorizationStatuses,
            platform,
        }) => {
            return (
                <>
                    <TextInput
                        name=""
                        readOnly={false}
                        onChange={setEndpoint}
                        value={endpoint}
                    />
                    <pre>{JSON.stringify(debug, null, 2)}</pre>
                    <pre>{JSON.stringify(dataDirectory, null, 2)}</pre>
                    {platform === "ios" && (
                        <div style={{marginTop: 15}}>
                            <h3 style={{marginBottom: 10}}>
                                Apple Health Authorization
                            </h3>
                            <pre>
                                Request Status: {healthKitStatus ?? "checking..."}
                            </pre>
                            <h4 style={{marginTop: 10, marginBottom: 5}}>
                                Individual Permissions:
                            </h4>
                            <pre>{JSON.stringify(authorizationStatuses, null, 2)}</pre>
                        </div>
                    )}
                </>
            );
        },
    )
    .withActions((props) => [
        {
            icon: "remove",
            title: "Delete Account",
            onClick: () =>
                props.confirmAndDeleteProfile(
                    props.profile,
                    props.subscription?.type == "apple",
                ),
        },
        {
            icon: "save",
            title: "Save",
            onClick: async () => {
                if (props.showSettings === true) {
                    utils.setAPIEndpoint(props.endpoint);
                    props.notify("Settings updated");
                    return;
                }

                const response = await forms.submitForm(
                    props.form,
                    server.core.rpc.update_profile,
                );

                if (response?.type === "success") {
                    props.notify("Profile updated");
                    void props.dispatch(fetchProfile());
                }
            },
        },
    ])
    .withOptions(() => ({
        title: "Profile",
    }));
